/**
 * CR173: React hook for subscribing to preview config changes
 * 
 * Provides reactive access to the unified preview store with
 * automatic re-renders when config changes.
 */

import { useState, useEffect, useCallback, useSyncExternalStore } from "react";
import { 
  previewConfigStore, 
  type PreviewConfig,
  type PreviewUpdateSource,
  type DealCardOverrides,
  computeEffectivePreviewConfig,
} from "@/stores/previewConfigStore";
import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";
import type { LayoutJson } from "@/lib/layout-studio/types";
import type { DealCardUXConfig } from "@/lib/layout-studio/dealCardUXTypes";

// ================================================================
// MAIN HOOK
// ================================================================

/**
 * Subscribe to the unified preview config store.
 * Re-renders component when any config changes.
 */
export function usePreviewConfig() {
  const config = useSyncExternalStore(
    (onStoreChange) => previewConfigStore.subscribe(() => onStoreChange()),
    () => previewConfigStore.getConfig(),
    () => previewConfigStore.getConfig()
  );
  
  // Memoized setters
  const setGlobalTheme = useCallback((tokens: GlobalThemeTokens | null) => {
    previewConfigStore.setGlobalTheme(tokens);
  }, []);
  
  const setLayout = useCallback((layout: LayoutJson | null) => {
    previewConfigStore.setLayout(layout);
  }, []);
  
  const setDealCardUX = useCallback((ux: DealCardUXConfig | null) => {
    previewConfigStore.setDealCardUX(ux);
  }, []);
  
  const setDealCardOverrides = useCallback((dealType: string, overrides: DealCardOverrides) => {
    previewConfigStore.setDealCardOverrides(dealType, overrides);
  }, []);
  
  const setPreviewControls = useCallback((controls: { 
    viewport?: "desktop" | "mobile"; 
    previewSource?: PreviewConfig["previewSource"] 
  }) => {
    previewConfigStore.setPreviewControls(controls);
  }, []);
  
  const getDebugSnapshot = useCallback(() => {
    return previewConfigStore.getDebugSnapshot();
  }, []);
  
  const effectiveConfig = computeEffectivePreviewConfig(config);
  
  return {
    config,
    effectiveConfig,
    setGlobalTheme,
    setLayout,
    setDealCardUX,
    setDealCardOverrides,
    setPreviewControls,
    getDebugSnapshot,
  };
}

// ================================================================
// SELECTIVE SUBSCRIPTION HOOKS
// ================================================================

/**
 * Subscribe to only global theme changes (for Theme Labs)
 */
export function usePreviewGlobalTheme(): {
  theme: GlobalThemeTokens | null;
  setTheme: (tokens: GlobalThemeTokens | null) => void;
  version: number;
} {
  const { config, setGlobalTheme } = usePreviewConfig();
  return {
    theme: config.globalTheme,
    setTheme: setGlobalTheme,
    version: config.version,
  };
}

/**
 * Subscribe to only layout changes (for Layout Studio)
 */
export function usePreviewLayout(): {
  layout: LayoutJson | null;
  dealCardUX: DealCardUXConfig | null;
  setLayout: (layout: LayoutJson | null) => void;
  setDealCardUX: (ux: DealCardUXConfig | null) => void;
  version: number;
} {
  const { config, setLayout, setDealCardUX } = usePreviewConfig();
  return {
    layout: config.layout,
    dealCardUX: config.dealCardUX,
    setLayout,
    setDealCardUX,
    version: config.version,
  };
}

/**
 * Subscribe to only deal card overrides (for Deal Cards tab)
 */
export function usePreviewDealCards(): {
  overrides: Record<string, DealCardOverrides>;
  activeDealType: string | null;
  setOverrides: (dealType: string, overrides: DealCardOverrides) => void;
  version: number;
} {
  const { config, setDealCardOverrides } = usePreviewConfig();
  return {
    overrides: config.dealCardOverrides,
    activeDealType: config.activeDealType,
    setOverrides: setDealCardOverrides,
    version: config.version,
  };
}

/**
 * Get debug info for preview instrumentation panel
 */
export function usePreviewDebug() {
  const { config, getDebugSnapshot } = usePreviewConfig();
  
  return {
    version: config.version,
    lastUpdatedAt: config.lastUpdatedAt,
    lastUpdatedBy: config.lastUpdatedBy,
    snapshot: getDebugSnapshot(),
  };
}
